export const CHECKLIST_RATING_MIN = 1;
export const CHECKLIST_RATING_MAX = 10;

export const RATING_INPUT_MODES = ["input", "dropdown", "horizontal", "vertical"] as const;
export type RatingInputMode = (typeof RATING_INPUT_MODES)[number];

export function resolveRatingInput(
   elements: unknown,
   options?: { fallback?: RatingInputMode },
): RatingInputMode {
   const fallback = options?.fallback && (RATING_INPUT_MODES as readonly string[]).includes(options.fallback)
      ? options.fallback
      : "input";

   if (!elements || typeof elements !== "object") {
      return fallback;
   }

   const raw = (elements as Record<string, unknown>).ratingInput;
   if (typeof raw !== "string") {
      return fallback;
   }

   const normalized = raw.trim().toLowerCase();
   if ((RATING_INPUT_MODES as readonly string[]).includes(normalized)) {
      return normalized as RatingInputMode;
   }

   return fallback;
}

export function isOverlayRatingInput(mode: RatingInputMode): boolean {
   return mode === "horizontal" || mode === "vertical";
}

/** Map an admin shortcut digit (0-9) to a rating value, or null if not a digit. */
export function adminDigitToRating(key: string): number | null {
   if (!/^[0-9]$/.test(key)) {
      return null;
   }

   return applyChecklistRatingDigit("", key);
}

export function isValidChecklistRating(value: number): boolean {
   return Number.isFinite(value) && value >= CHECKLIST_RATING_MIN && value <= CHECKLIST_RATING_MAX;
}

/**
 * Apply one digit key (0-9) to the current in-field display.
 * 0 always commits 10. The only two-digit value is 10 (1 then 0).
 */
export function applyChecklistRatingDigit(currentDisplay: string, digit: string): number {
   if (!/^[0-9]$/.test(digit)) {
      return 0;
   }

   if (digit === "0") {
      return 10;
   }

   const trimmed = String(currentDisplay ?? "").trim();
   if (trimmed === "1") {
      return parseInt(digit, 10);
   }

   return parseInt(digit, 10);
}

/** Whether admin auto-advance should move to the next row after this digit entry. */
export function shouldAdvanceChecklistRatingEntry(currentDisplay: string, result: number): boolean {
   if (result === 10) {
      return true;
   }

   if (result >= 2 && result <= 9) {
      return true;
   }

   // Lone 1 on an empty field may become 10 — stay on the row.
   if (result === 1 && trimmedDisplay(currentDisplay) === "") {
      return false;
   }

   // Replacing an existing value with 1 still completes the entry.
   return result === 1;
}

export function sanitizeChecklistRating(raw: unknown): number {
   if (raw === null || raw === undefined || raw === "") {
      return 0;
   }

   const str = String(raw).trim();
   if (str === "0") {
      return 10;
   }

   const parsed = parseInt(str, 10);
   if (!Number.isFinite(parsed) || !isValidChecklistRating(parsed)) {
      return 0;
   }

   return parsed;
}

export function formatChecklistRatingDisplay(rating: number): string {
   if (!isValidChecklistRating(rating)) {
      return "";
   }

   return String(rating);
}

function trimmedDisplay(currentDisplay: string): string {
   return String(currentDisplay ?? "").trim();
}
